import { BaseQueryApi } from '@reduxjs/toolkit/dist/query';

import type { RootState } from '@/features/store';
import type { IItem } from '@/interfaces/grpc.interfaces';
import deepCopy from '@/utils/deepCopy';

import { referencesActions } from '..';

import getPartiallyExtendedItems from './getPartiallyExtendedItems';
import modifyReferences from './modifyReferences';

/**
 * Расширяет значения типа IReference в переданном массиве объектов, действительными значениями
 * @param api
 * @param items массив объектов, с полями, которые содержат ссылки на IReference
 * @returns массив объектов с расширенными данными по IReference.
 * Значение IReference может быть не расширено, если оно отсутствует на сервере.
 */
async function getExtendedItems<Item extends IItem, ExtendedItem extends IItem = Item>(
  api: BaseQueryApi,
  items: Item[],
) {
  const state = api.getState() as RootState;

  // Сначала выполняется поиск существующих значений свойств IReference на стейте.
  const { partiallyExtendedItems, nonExtendedReferences, uniqueNonExtendedReferences } =
    getPartiallyExtendedItems(state.references, deepCopy(items));

  // Для не найденных значений свойств выполняется их поиск на сервере.
  if (uniqueNonExtendedReferences.length) {
    await api
      .dispatch(
        referencesActions.fetchReferences({
          envId: state.config.envId,
          spaceId: state.config.spaceId,
          references: uniqueNonExtendedReferences,
        }),
      )
      .unwrap();

    const stateWithUniqueRefs = api.getState() as RootState;
    // Дозаполняем значения IReference (nonExtendedReferences), значениями полученными с сервера.
    modifyReferences(stateWithUniqueRefs.references, nonExtendedReferences);
  }

  return partiallyExtendedItems as ExtendedItem[];
}

export default getExtendedItems;
